import EditableInput from "@/components/ui/editable-input";
import { Lock, Save, Check, X, Eye, EyeOff, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState } from "react";
import { toast } from "@/hooks/useToast";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useI18n } from '@/hooks/useI18n';

// Security section component with separate edit state
interface SecuritySectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
  isSaving?: boolean;
  onSave?: () => void;
}

interface PasswordRequirement {
  id: string;
  text: string;
  validator: (password: string) => boolean;
}

const SecuritySection = ({ 
  isEditing = false,
  onEditToggle,
  onCancelEdit,
  isSaving = false,
  onSave
}: SecuritySectionProps) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  // Get updatePassword from hook
  const { updatePassword } = useUserProfile();
  const { t } = useI18n();

  // Password requirements with validators
  const passwordRequirements: PasswordRequirement[] = [
    {
      id: 'length',
      text: t('security.requirementLength'),
      validator: (pwd) => pwd.length >= 8
    },
    {
      id: 'uppercase',
      text: t('security.requirementUppercase'),
      validator: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      id: 'lowercase',
      text: t('security.requirementLowercase'),
      validator: (pwd) => /[a-z]/.test(pwd)
    },
    {
      id: 'number',
      text: t('security.requirementNumber'),
      validator: (pwd) => /\d/.test(pwd)
    },
    {
      id: 'special',
      text: t('security.requirementSpecial'),
      validator: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    }
  ];

  const handlePasswordChange = (value: string) => {
    setPassword(value);
  };

  const handlePasswordFocus = () => {
    if (isEditing) {
      setTooltipOpen(true);
    }
  };

  const handlePasswordBlur = () => {
    setTooltipOpen(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSavePassword = async () => {
    if (!password.trim()) {
      toast({
        title: t('common.error'),
        description: t('security.enterNewPassword'),
        variant: "destructive",
      });
      return;
    }
    
    // Check if all requirements are met
    const allRequirementsMet = passwordRequirements.every(req => req.validator(password));
    if (!allRequirementsMet) {
      toast({
        title: t('common.error'),
        description: t('security.passwordNotMeetRequirements'),
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Update password using auth context
      const result = await updatePassword(password);

      if (!result.success) {
        console.error('Error updating password:', result.error);
        
        // Handle specific error cases
        if (result.error?.includes('Riautenticazione richiesta')) {
          toast({
            title: t('security.reauthenticateTitle'),
            description: t('security.reauthenticateDesc'),
            variant: "destructive",
          });
          // Redirect to login page
          window.location.href = '/login';
          return;
        }
        
        toast({
          title: t('common.error'),
          description: result.error || t('security.passwordUpdateError'),
          variant: "destructive",
        });
        return;
      }
      
      // Success
      toast({
        title: t('common.success'),
        description: t('security.passwordUpdatedSuccess'),
      });
      
      // Clear password field after successful save
      setPassword('');
      setShowPassword(false);
      
      // Call the parent save handler
      if (onSave) {
        onSave();
      }
      
    } catch (error) {
      console.error('Unexpected error updating password:', error);
      toast({
        title: t('common.error'),
        description: t('security.passwordUpdateUnexpected'),
        variant: "destructive",
      });
    }
  };

  const getRequirementStatus = (requirement: PasswordRequirement) => {
    if (!password) return 'pending';
    return requirement.validator(password) ? 'valid' : 'invalid';
  };

  const getRequirementIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <Check className="h-3 w-3 text-green-500" />;
      case 'invalid':
        return <X className="h-3 w-3 text-red-500" />;
      default:
        return <div className="h-3 w-3 rounded-full border border-gray-300" />;
    }
  };

  const getRequirementTextColor = (status: string) => {
    switch (status) {
      case 'valid':
        return 'text-green-700';
      case 'invalid':
        return 'text-red-700';
      default:
        return 'text-gray-600';
    }
  };

  const isPasswordValid = passwordRequirements.every(req => req.validator(password));

  // Tooltip content component
  const PasswordRequirementsTooltip = () => (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-900">
        Requisiti Password
      </h4>
      <ul className="text-sm space-y-1">
        {passwordRequirements.map((requirement) => {
          const status = getRequirementStatus(requirement);
          return (
            <li key={requirement.id} className="flex items-center gap-2">
              {getRequirementIcon(status)}
              <span className={getRequirementTextColor(status)}>
                {requirement.text}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
                    <Lock className="h-5 w-5" />
                    {t('security.title')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('security.description')}
                  </p>
          </div>
          {onEditToggle && onCancelEdit && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={onCancelEdit}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSavePassword}
                    disabled={!password.trim() || isSaving || !isPasswordValid}
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isSaving ? t('common.saving') : t('common.save')}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onEditToggle}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Modifica
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Password with tooltip */}
        <div className="space-y-2">
          <Tooltip open={tooltipOpen}>
            <TooltipTrigger asChild>
              <div>
                <EditableInput
                  label="Password"
                  value={isEditing ? password : ""}
                  type={isEditing && showPassword ? "text" : "password"}
                  icon={Lock}
                  isEditing={isEditing}
                  onChange={handlePasswordChange}
                  placeholder={isEditing ? "Inserisci la nuova password" : "••••••••••••••••"}
                  onFocus={handlePasswordFocus}
                  onBlur={handlePasswordBlur}
                  rightIcon={isEditing ? (
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  ) : undefined}
                />
              </div>
            </TooltipTrigger>
            {isEditing && (
              <TooltipContent side="top" className="w-64 p-3">
                <PasswordRequirementsTooltip />
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SecuritySection;
